import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import MainHeading from "@/components/common/MainHeading";
import Card from "@/components/common/Card";
import CommonTable, { type TableColumn } from "@/components/common/CommonTable";
import CommonFilter from "@/components/common/CommonFilter";
import TablePagination from "@/components/common/TablePagination";
import TableActions from "@/components/common/TableActions";
import { BranchService } from "@/services/Branch/BranchSevice";
import type { branch } from "@/interface/branch.interface";
import type { IFilter, IPageInfo } from "@/interface/common.interface";
import { GetFilterUrl } from "@/utility/helper";
import { ThrowError } from "@/api/callApi";
import { RoutesLink } from "@/utility/RouteLinks";
import { Building2 } from "lucide-react";

const TeamsPayslip: React.FC = () => {
  const navigate = useNavigate();
  const [branches, setBranches] = useState<branch[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [filter, setFilter] = useState<IFilter>({
    search: "",
  });

  const [paginationData, setPaginationData] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  const handleSearchChange = (value: string) => {
    setFilter((prev) => ({ ...prev, search: value }));
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleChangePageSize = (value: number) => {
    setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }));
  };

  const fetchBranches = async (
    currentPage = paginationData.currentPage,
    currentLimit = paginationData.pageSize,
  ) => {
    setIsLoading(true);
    try {
      const paramsURL = GetFilterUrl(filter);
      const PATH = `pageNumber=${currentPage}&pageSize=${currentLimit}${paramsURL ? `&${paramsURL}` : ""}`;
      const response = await BranchService.getBranch(PATH);
      if (response?.status) {
        setBranches(response.data?.branch || []);
        setPaginationData((prev) => ({
          ...prev,
          total: response.data?.pageInfo?.total || 0,
          totalPages: response.data?.pageInfo?.totalPages || 0,
        }));
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches(paginationData.currentPage, paginationData.pageSize);
  }, [paginationData.currentPage, paginationData.pageSize, filter]);

  const columns: TableColumn<branch>[] = [
    {
      key: "sNo",
      header: "S.No",
      render: (_item: branch, index: number) => (
        <span className="text-muted-foreground font-medium text-sm">
          {String(
            (paginationData.currentPage - 1) * paginationData.pageSize +
            index +
            1,
          ).padStart(2, "0")}
        </span>
      ),
    },
    {
      key: "name",
      header: "Office Name",
      render: (item: branch) => (
        <div>
          <p className="font-semibold text-slate-800">{item.name}</p>
          <p className="text-xs text-slate-500">
            {item.city && item.state ? `${item.city}, ${item.state}` : "N/A"}
          </p>
        </div>
      ),
    },
    {
      key: "aliasId",
      header: "Alias ID",
      render: (item: branch) => (
        <span className="font-mono text-sm text-slate-700">
          {item.aliasId || "-"}
        </span>
      ),
    },
    {
      key: "phone",
      header: "Phone",
      render: (item: branch) => (
        <span className="text-slate-700">{item.phone || "-"}</span>
      ),
    },
    {
      key: "email",
      header: "Email",
      render: (item: branch) => (
        <span className="text-slate-700">{item.email || "-"}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (item: branch) => {
        const isActive = item.status === true;
        return (
          <span
            className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${isActive
              ? "text-green-600 border-green-600 bg-green-50"
              : "text-destructive border-destructive bg-destructive/10"
              }`}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "Actions",
      render: (item: branch) => (
        <TableActions
          onView={() => navigate(`${RoutesLink.branchUserPayslip}/${item.id}`)}
          viewLabel="View Users"
          actions={[]}
        />
      ),
    },
  ];



  return (
    <Layout>
      <div className="space-y-6">
        <MainHeading title="Team Payslip" isBackButtonVisible={true} />

        {/* Branch Table Card */}
        <Card className="border-0 shadow-md">
          {/* Header Section */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-4 border-b border-slate-100">
            <div className="flex items-center gap-3.5">
              <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-100/80 flex items-center justify-center text-indigo-600 shadow-2xs shrink-0">
                <Building2 className="h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h3 className="text-xl font-bold text-slate-800 tracking-tight">
                    Branch Directory
                  </h3>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100/80">
                    {paginationData.total} {paginationData.total === 1 ? "Branch" : "Branches"} Available
                  </span>
                </div>
                <p className="text-xs text-slate-500 font-medium mt-0.5">
                  Select a branch to view, calculate and generate employee monthly payslips.
                </p>
              </div>
            </div>
          </div>

          <CommonFilter
            searchValue={filter.search || ""}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Search branch name "
            NoAdvanceFilters={true}
          />

          <CommonTable data={branches} columns={columns} loading={isLoading} />

          <TablePagination
            paginationData={paginationData}
            setPaginationData={setPaginationData}
            handleChangePageSize={handleChangePageSize}
            isLoading={isLoading}
          />
        </Card>
      </div>
    </Layout>
  );
};

export default TeamsPayslip;
